type Palette = {
  background: string;
  text: string;
  mutedText: string;
  primary: string;
  primaryText: string;
  primaryHover: string;
  primaryBorder: string;
  border: string;
};

/** Compact only changes the notice; the shared preferences dialog is untouched. */
export function buildCompactAppearanceStyles(): string {
  const cm = 'html[data-consentpop-layout="compact"] #cc-main .cm.cm';
  return `
    ${cm} {
      width: max-content !important;
      max-width: calc(100vw - 48px) !important;
      overflow: visible !important;
      border-radius: 12px !important;
    }
    ${cm}.cm--right { right: max(24px, env(safe-area-inset-right)) !important; left: auto !important; }
    ${cm}.cm--left { left: max(24px, env(safe-area-inset-left)) !important; right: auto !important; }
    ${cm} .cm__body {
      display: block !important;
      padding: 16px 24px 16px 18px !important;
      overflow: visible !important;
    }
    ${cm} .cm__texts { margin: 0 !important; text-align: left !important; }
    ${cm} .cm__title {
      display: flex !important;
      align-items: center;
      flex-wrap: wrap;
      gap: 0 .3em;
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      letter-spacing: 0;
      text-align: left !important;
    }
    ${cm} .cm__title::before {
      width: 24px;
      height: 24px;
      margin-inline-end: 8px;
      flex: 0 0 24px;
    }
    ${cm} .cm__desc, ${cm} .cm__footer, ${cm} .cm__btns { display: none !important; }
    ${cm} .cm__btn[data-role="show"] {
      order: 0 !important;
      display: inline !important;
      width: auto !important;
      min-width: 0 !important;
      min-height: 0 !important;
      height: auto !important;
      align-self: center !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      color: inherit !important;
      font-size: inherit !important;
      font-weight: inherit !important;
      line-height: inherit !important;
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-color: var(--cc-secondary-color);
      overflow-wrap: anywhere;
    }
    ${cm} > .cm__btn[data-role="all"] {
      display: grid !important;
      place-items: center;
      position: absolute;
      top: -12px;
      inset-inline-end: -12px;
      width: 26px !important;
      height: 26px !important;
      min-width: 26px !important;
      min-height: 26px !important;
      padding: 0 !important;
      border: 1px solid var(--cc-separator-border-color) !important;
      border-radius: 50% !important;
      background: var(--cc-bg) !important;
      color: var(--cc-secondary-color) !important;
      overflow: visible !important;
    }
    ${cm} > .cm__btn[data-role="all"]::after {
      content: "";
      position: absolute;
      inset: -9px;
      border-radius: 50%;
    }
    ${cm} > .cm__btn[data-role="all"] svg { width: 12px; height: 12px; }
    ${cm} > .cm__btn[data-role="all"]:hover { color: var(--cc-primary-color) !important; }
    ${cm} .cm__btn[data-role="show"]:hover { text-decoration-color: currentColor; }
    @media (pointer: coarse) {
      ${cm} .cm__btn[data-role="show"] { padding-block: 10px !important; margin-block: -10px; }
    }
  `;
}

/** Shared card/bar styling. Geometry is independent of the selected colors. */
export function buildSimpleAppearanceStyles(palette: Palette, bar: boolean): string {
  const scope = 'html[data-consentpop-theme="simple"] #cc-main';
  const cm = `${scope} .cm.cm`;
  return `
    ${scope} { --cc-font-family: inherit; }
    /* Follow the host body font while isolating headings and controls from host rules. */
    ${scope}, ${scope} * {
      font-family: inherit !important;
    }

    ${cm} {
      width: ${bar?`min(800px, calc(100vw - 40px))`:`min(350px, calc(100vw - 40px))`} !important;
      max-width: ${bar?`800px`:`350px`} !important;
      min-height: 0 !important;
      max-height: calc(100dvh - 40px);
      top: auto !important;
      bottom: max(20px, env(safe-area-inset-bottom)) !important;
      ${bar?`left: 20px !important; right: 20px !important; margin-inline: auto !important;`:``}
      border: 1px solid ${palette.border} !important;
      border-radius: ${bar?9:12}px !important;
      background: ${palette.background} !important;
      color: ${palette.text};
      box-shadow: 0 8px 30px rgba(15, 23, 42, .12);
      font-family: inherit;
      transform: none !important;
    }
    ${cm}.cm--left { left: max(20px, env(safe-area-inset-left)) !important; right: auto !important; }
    ${cm}.cm--right { right: max(20px, env(safe-area-inset-right)) !important; left: auto !important; }
    ${cm}.cm--center { left: 0 !important; right: 0 !important; margin-inline: auto !important; }
    html.show--consent[data-consentpop-theme="simple"] #cc-main .cc--anim .cm.cm { transform: none !important; }
    ${cm}::before, ${cm}::after, ${cm} .cm__texts::before { content: none !important; }
    ${cm} .cm__body {
      display: grid !important;
      grid-template-columns: ${bar?`minmax(0, 1fr) 150px`:`repeat(2, minmax(0, 1fr))`};
      gap: ${bar?`8px 26px`:`8px`} !important;
      align-items: center;
      width: 100%;
      min-width: 0;
      min-height: 0 !important;
      max-width: none;
      padding: 29px 23px 0 !important;
      overflow: auto;
    }
    ${cm} .cm__texts {
      grid-column: ${bar?`1`:`1 / -1`};
      ${bar?`grid-row: 1 / span 2;`:`margin-bottom: 13px !important;`}
      min-width: 0;
      padding: 0 !important;
      width: auto;
      flex: none;
      display: block !important;
      border: 0 !important;
      text-align: ${bar ? "left" : "center"};
    }
    ${cm} .cm__title {
      display: block !important;
      font-weight: 700 !important;
      font-size: ${bar?18:20}px !important;
      line-height: 1.25 !important;
      letter-spacing: -.4px;
      color: ${palette.text} !important;
      text-transform: none;
      text-align: ${bar ? "left" : "center"} !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    ${cm} .cm__desc {
      display: block !important;
      max-width: none;
      max-height: none;
      padding: 0 !important;
      margin-top: 12px !important;
      font-weight: 400 !important;
      font-size: 13px !important;
      line-height: 1.65 !important;
      color: ${palette.mutedText} !important;
      white-space: normal;
      overflow-wrap: anywhere;
      text-align: ${bar ? "left" : "center"} !important;
    }
    ${cm} .cm__btns, ${cm} .cm__btn-group { display: contents !important; }
    ${cm} .cm__btn[data-role],
    ${scope} .pm.pm .pm__btn {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 44px !important;
      height: auto !important;
      padding: 11px 12px !important;
      margin: 0 !important;
      border: 1px solid ${palette.border} !important;
      border-radius: 6px !important;
      background: ${palette.background} !important;
      color: ${palette.text} !important;
      font-weight: 500 !important;
      font-size: 13px !important;
      line-height: 1.3 !important;
      text-transform: none;
      white-space: normal !important;
      overflow-wrap: anywhere;
      box-shadow: none !important;
      align-self: stretch !important;
    }
    ${cm} .cm__btn[data-role="necessary"] { order: 1; ${bar?`grid-column: 2; grid-row: 1;`:``} }
    ${cm} .cm__btn[data-role="all"] {
      order: 2;
      ${bar?`grid-column: 2; grid-row: 2;`:``}
    }
    ${cm} .cm__btn[data-role="all"],
    ${scope} .pm.pm .pm__btn[data-role="all"] {
      background: ${palette.primary} !important;
      border-color: ${palette.primaryBorder} !important;
      color: ${palette.primaryText} !important;
    }
    ${cm} .cm__btn[data-role="show"] {
      order: -1 !important;
      display: inline-flex !important;
      align-items: center;
      justify-content: flex-start !important;
      gap: 4px;
      flex: 0 0 auto !important;
      width: auto !important;
      min-height: 36px !important;
      height: 36px !important;
      padding: 8px 0 8px 6px !important;
      border: 0 !important;
      border-radius: 6px !important;
      background: transparent !important;
      color: ${palette.mutedText} !important;
      font-size: 10px !important;
      text-decoration: none;
    }
    ${cm} .cm__btn[data-role="show"] .cp__icon { width: 12px; height: 12px; }

    ${cm} .cm__btn:hover,
    ${scope} .pm.pm .pm__btn:hover { filter: none !important; }
    ${cm} .cm__btn[data-role="all"]:hover,
    ${scope} .pm.pm .pm__btn[data-role="all"]:hover {
      background: ${palette.primaryHover} !important;
      color: ${palette.primaryText} !important;
      border-color: ${palette.primaryBorder} !important;
    }
    ${cm} .cm__footer {
      display: flex !important;
      flex-direction: row !important;
      align-items: center;
      justify-content: space-between !important;
      gap: 12px;
      margin-top: 12px !important;
      padding: 0 23px 15px !important;
      background: transparent !important;
      border: 0 !important;
    }
    ${cm} .cm__links, ${cm} .cm__link-group, ${cm} .cp__attribution { justify-content: flex-end !important; }
    ${cm} .cm__links { margin-left: auto !important; padding: 0 6px 0 0 !important; }
    ${scope} .pm.pm { border-radius: 12px; font-family: inherit; }
    ${scope} .pm.pm .pm__footer,
    ${scope} .pm.pm .pm__btn-group { gap: 8px; }
    @media (max-width: 640px) {
      ${cm} {
        width: ${bar?`calc(100vw - 24px)`:`min(350px, calc(100vw - 24px))`} !important;
        max-width: ${bar?`calc(100vw - 24px)`:`350px`} !important;
        left: ${bar?`12px`:`auto`} !important;
        right: 12px !important;
        bottom: max(12px, env(safe-area-inset-bottom)) !important;
        max-height: calc(100dvh - 24px);
        transform: none !important;
      }
      ${cm}.cm--left { left: 12px !important; right: auto !important; }
      ${cm}.cm--right { right: 12px !important; left: auto !important; }
      ${cm}.cm--center { left: 0 !important; right: 0 !important; margin-inline: auto !important; }
      ${cm} .cm__body { grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 26px 20px 0 !important; }
      ${cm} .cm__texts { grid-column: 1 / -1; grid-row: auto; margin-bottom: 13px !important; }
      ${cm} .cm__btn[data-role="necessary"], ${cm} .cm__btn[data-role="all"] { grid-column: auto; grid-row: auto; }
      ${cm} .cm__footer { padding: 0 20px 14px !important; }
    }
  `;
}
